import React, { memo } from 'react';
import { cn, Icons } from '@ohif/ui-next';
import { useSystem } from '@ohif/core';
import { UploadRow, UploadStatus } from '../../utils/uploadTypes';

interface FileItemProps {
  row: UploadRow;
  isCurrentFile: boolean;
}

const FileItem = memo(
  ({ row, isCurrentFile }: FileItemProps) => {
    const { servicesManager } = useSystem();

    const ProgressLoadingBar = (
      servicesManager.services.customizationService as AppTypes.CustomizationService
    ).getCustomization('ui.progressLoadingBar') as React.ComponentType<{ progress?: number }>;

    return (
      <div
        className={cn('mb-1 rounded-lg border px-4 py-2', {
          'border-primary-light': isCurrentFile || row.status === UploadStatus.Success,
          'border-destructive': row.status === UploadStatus.Failed,
          'border-muted-foreground': row.status === UploadStatus.Cancelled,
          'border-input': row.status === UploadStatus.NotStarted,
        })}
        data-cy="sycai-upload-row"
      >
        <div className="mb-1 max-w-[300px]">
          <span
            className="text-foreground block overflow-hidden text-ellipsis whitespace-nowrap text-sm"
            title={row.name}
          >
            {row.name}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <ProgressLoadingBar progress={row.progress} />
          </div>
          <span className="text-foreground ml-2 min-w-[45px] text-right text-sm">
            {row.progress}%
          </span>
          <div className="group relative h-6 w-6">
            {row.status === UploadStatus.Success && (
              <Icons.StatusSuccess className="text-primary-light" />
            )}
            {row.status === UploadStatus.Cancelled && (
              <Icons.AlertOutline className="text-muted-foreground" />
            )}
            {row.status === UploadStatus.InProgress && (
              <Icons.LoadingSpinner className="text-primary-light animate-spin" />
            )}
            {row.status === UploadStatus.NotStarted && (
              <Icons.Info className="text-muted-foreground" />
            )}
            {row.status === UploadStatus.Failed && (
              <>
                <Icons.StatusError className="text-destructive" />
                {row.error && (
                  <div className="absolute bottom-full right-0 z-50 mb-1 hidden group-hover:block">
                    <div className="w-[250px] rounded bg-black px-2 py-1 text-xs text-white">
                      <div className="whitespace-normal break-all">{row.error}</div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  },
  (prev, next) => prev.row === next.row && prev.isCurrentFile === next.isCurrentFile
);

FileItem.displayName = 'FileItem';

export default FileItem;
